
/**
 * Module dependencies.
 */

import { ChangeEvent, FocusEvent, ForwardedRef, forwardRef } from 'react';
import { FieldAlert } from '../../forms/field-alert';
import { FieldError } from '../../forms/field-error';
import { FormControl, FormControlWrapper, FormGroup, Label, MaxLength } from '../../forms/fields-styled-components';
import { InputProps, defaultNormalizeValue } from './input';
import { color } from 'react-components/styles';
import { ifProp } from 'styled-tools';
import { isEmpty } from 'lodash';
import { useField, useFormState } from '@seegno/react-forms';
import styled, { css } from 'styled-components';

/**
 * `StyledLabel` styled component.
 */

const StyledLabel = styled(Label)<{ disabled: boolean }>`
  background-color: ${color('gray300')};

  ${ifProp('disabled', css`
    background-color: ${color('gray100')};
  `)}
`;

/**
 * `StyledMaxLength` styled component.
 */

const StyledMaxLength = styled(MaxLength)`
  bottom: 0;
  padding-left: 0;
  padding-right: 0;
  padding-top: 0;
  position: absolute;
  right: 14px;
`;

/**
 * `Textarea` component.
 */

function Textarea(props: InputProps, ref: ForwardedRef<HTMLTextAreaElement>): JSX.Element {
  const {
    className,
    disabled = false,
    helpText,
    id,
    label,
    maxLength,
    name,
    normalizeValue = defaultNormalizeValue,
    parseValue,
    ...rest
  } = props;

  const { error, meta, onBlur, onChange, value, ...fieldProps } = useField(name);
  const { active } = meta || {};
  const { alreadySubmitted, isSubmitting } = useFormState();

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    const value = event.target.value;
    const parsedValue = parseValue ? parseValue(value) : value;

    onChange(!isEmpty(parsedValue) ? parsedValue : undefined);
  }

  function handleBlur(event: FocusEvent<HTMLTextAreaElement>) {
    const value = event.target.value;
    const parsedValue = parseValue ? parseValue(value) : value;
    const normalizedValue = normalizeValue(parsedValue);

    onChange(!isEmpty(normalizedValue) ? normalizedValue : undefined);
    onBlur(event);
  }

  const hasError = !!error && alreadySubmitted;
  const isDisabled = disabled || isSubmitting;

  return (
    <FormGroup
      className={className}
      disabled={isDisabled}
      hasAlert={hasError || !!helpText}
    >
      <FormControlWrapper
        disabled={isDisabled}
        hasError={hasError}
        hasFocus={active}
      >
        <StyledLabel
          disabled={isDisabled}
          htmlFor={id ?? name}
          isFloating={Boolean(value) || active}
        >
          {label}
        </StyledLabel>

        <FormControl
          as={'textarea'}
          disabled={isDisabled}
          id={id ?? name}
          name={name}
          ref={ref}
          value={value || ''}
          {...rest}
          {...fieldProps}
          onBlur={handleBlur}
          onChange={handleChange}
        />

        {maxLength && (
          <StyledMaxLength disabled={isDisabled}>
            {`${value?.length ?? 0}/${maxLength}`}
          </StyledMaxLength>
        )}
      </FormControlWrapper>

      {helpText && (
        <FieldAlert isOpen={!hasError}>
          {helpText}
        </FieldAlert>
      )}

      <FieldError
        error={error}
        isOpen={hasError}
        name={name}
      />
    </FormGroup>
  );
}

/**
 * Forwarded `Textarea`.
 */

const ForwardedTextarea = forwardRef<HTMLTextAreaElement, InputProps>(Textarea);

/**
 * Export `Textarea` component.
 */

export { ForwardedTextarea as Textarea };
